import { Injectable } from '@angular/core';
import { getId } from '@cawita/core-front';
import { CwtAuthStoreService } from '@cawita/core-front/auth';
import { AsyncStore } from '@cawita/core-front/state';
import { Profile, User } from '@shared/models';
import { finalize, tap } from 'rxjs/operators';
import { AlertService } from '../../services/commons/alert.service';
import { ProfileAPI } from './profile.api';

export interface ProfileState {
    profile?: Profile;
}

@Injectable({ providedIn: 'root' })
export class LoggedProfileStore extends AsyncStore<ProfileState> {
    constructor(
        private api: ProfileAPI,
        private alert: AlertService,
        private auth: CwtAuthStoreService
    ) { super({}) }

    public init(userId: string) {
        this.setInitialized(false);
        this.setLoading(true);
        return this.api.getProfile(userId, {
            options: {
                projection: 'forProfileDetailForApp'
            }
        }).pipe(
            finalize(() => {
                this.setLoading(false);
                this.setInitialized(true);
            }),
            tap((profile) => this.setState(c => ({
                ...c,
                profile
            })))
        );
    }

    public refresh() {
        const { profile } = this.getValue();
        return this.init(getId(profile.user));
    }

    public updateProfile(data: any) {
        const { profile } = this.getValue();
        return this.api.updateProfile(getId(profile), data, {
            options: {
                projection: 'forProfileDetailForApp'
            }
        }).pipe(
            tap((profile) => this.setState(c => ({
                ...c,
                profile
            })))
        );
    }

    public updatePicture(file: File) {
        return this.api.updateLoggedUserPicture(file).pipe(
            tap((user) => this.setState(c => ({
                ...c,
                profile: new Profile({
                    ...c.profile,
                    user: new User({
                        ...c.profile.user,
                        ...user,
                    })
                })
            })))
        );
    }

    public async deleteProfile() {
        const shouldDelete = await this.alert.confirm({
            title: 'alert.delete-your-profile',
            subtitle: 'alert.this-action-can-not-be-undone',
            message: 'alert.all-your-information-willl-be-deleted-and-you-will-lose-access-to-the-app'
        });
        if (!shouldDelete) return;
        const { profile } = this.getValue();
        await this.api.deleteProfile(getId(profile)).toPromise();
        this.auth.logout();
    }

    public block(profileToBlock: Profile) {
        const { profile } = this.getValue();
        return this.api.addProfileToBlockList(getId(profile), profileToBlock, {
            options: {
                projection: 'forProfileDetailForApp'
            }
        }).pipe(
            tap((profile) => this.setState(c => ({
                ...c,
                profile: new Profile({
                    ...c.profile,
                    blockedProfiles: profile.blockedProfiles
                })
            })))
        );
    }

    public unblock(profileToUnblock: Profile) {
        const { profile } = this.getValue();
        return this.api.removeProfileFromBlockList(getId(profile), profileToUnblock, {
            options: {
                projection: 'forProfileDetailForApp'
            }
        }).pipe(
            tap((profile) => this.setState(c => ({
                ...c,
                profile: new Profile({
                    ...c.profile,
                    blockedProfiles: profile.blockedProfiles
                })
            })))
        );
    }
}
